<template>
    <v-row justify="center">
        <v-dialog v-model="show_email_sending_dialog" max-width="760" persistent scrollable>
            <v-card>
                <v-card-title>Email Sending (Mailgun)</v-card-title>
                <v-card-text>
                    <v-skeleton-loader v-if="loading" type="list-item-three-line@2"></v-skeleton-loader>
                    <template v-else>
                        <p class="grey--text text--darken-1">
                            Emails sent from an address on one of these domains (e.g. bookings@stagit.ie for
                            <b>mg.stagit.ie</b>) go through that Mailgun domain with its own sending key, signed for
                            that domain. Every other email uses the default Mailgun
                            (<b>{{ settings.default_domain || 'from the server settings' }}</b>), as before.
                        </p>
                        <div v-for="(row, index) in rows" :key="index" class="d-flex align-start" style="gap:12px;">
                            <v-text-field v-model="row.domain" label="Mailgun domain" placeholder="mg.stagit.ie"
                                outlined dense style="max-width:240px;"></v-text-field>
                            <v-text-field v-model="row.key" label="Sending key" outlined dense type="password"
                                autocomplete="new-password"
                                :placeholder="row.has_key ? 'Saved. Leave blank to keep it' : 'Mailgun → domain → Sending keys'"
                                :hint="row.has_key ? 'A key is saved (never shown)' : ''" persistent-hint></v-text-field>
                            <v-btn icon class="mt-1" title="Remove domain and its key" @click="rows.splice(index, 1)">
                                <v-icon>mdi-delete-outline</v-icon>
                            </v-btn>
                        </div>
                        <v-btn text small color="#3988AA" class="mb-4 pl-0"
                            @click="rows.push({ domain: '', key: '', has_key: false })">
                            <v-icon small class="mr-1">mdi-plus</v-icon>Add domain
                        </v-btn>
                        <v-select v-model="endpoint" :items="regions" item-text="text" item-value="value"
                            label="Mailgun region" outlined dense></v-select>

                        <v-alert dense text :type="settings.bookings_email_route ? 'success' : 'info'">
                            <template v-if="settings.bookings_email">
                                {{ settings.bookings_email }} currently sends through
                                <b>{{ settings.bookings_email_route || `the default Mailgun (${settings.default_domain})` }}</b>.
                            </template>
                            <template v-else>No Bookings Email is set in Company Settings.</template>
                        </v-alert>
                        <div class="d-flex align-center" style="gap:12px;">
                            <v-text-field v-model="test_from" label="Test email from" outlined dense hide-details
                                :placeholder="settings.bookings_email"></v-text-field>
                            <v-btn outlined color="#3988AA" class="custom-buttons" :loading="testing"
                                @click="sendTest()">Send test to {{ get_user ? get_user.email : 'me' }}</v-btn>
                        </div>
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="custom-buttons" text @click="$emit('closeEmailSendingDialog')">Close</v-btn>
                    <v-btn dark color="#3988AA" class="custom-buttons" :loading="saving" @click="save()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    props: ['show_email_sending_dialog'],
    data: () => ({
        loading: false,
        saving: false,
        testing: false,
        settings: {},
        rows: [],
        endpoint: 'api.mailgun.net',
        test_from: '',
        regions: [
            { text: 'US (api.mailgun.net)', value: 'api.mailgun.net' },
            { text: 'EU (api.eu.mailgun.net)', value: 'api.eu.mailgun.net' },
        ],
    }),
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
            get_user: 'auth/get_user',
        }),
    },
    watch: {
        show_email_sending_dialog(open) {
            if (open) this.load()
        },
    },
    methods: {
        apply(data) {
            this.settings = data
            this.rows = (data.domains || []).map(d => ({ domain: d.domain, key: '', has_key: d.has_key }))
            this.endpoint = data.mailgun_endpoint || 'api.mailgun.net'
        },
        async load() {
            this.loading = true
            try {
                const result = (await this.$axios.get(`admin/companies/get_mail_settings/${this.get_selected_company.id}`)).data
                if (result.response == true) this.apply(result.data)
            } finally {
                this.loading = false
            }
        },
        async save() {
            this.saving = true
            try {
                const payload = {
                    mailgun_endpoint: this.endpoint,
                    domains: this.rows.filter(r => r.domain.trim()).map(r => ({ domain: r.domain.trim(), key: r.key })),
                }
                const result = (await this.$axios.post(`admin/companies/update_mail_settings/${this.get_selected_company.id}`, payload)).data
                if (result.response == true) {
                    this.apply(result.data)
                    this.$toast.success(result.message)
                } else {
                    this.$toast.error(result.message || 'Failed to save')
                }
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Failed to save')
            } finally {
                this.saving = false
            }
        },
        async sendTest() {
            this.testing = true
            try {
                const result = (await this.$axios.post(`admin/companies/send_test_email/${this.get_selected_company.id}`,
                    { from: this.test_from || null })).data
                if (result.response == true) this.$toast.success(result.message, { timeout: 10000 })
                else this.$toast.error(result.message || 'Test email failed')
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Test email failed', { timeout: 15000 })
            } finally {
                this.testing = false
            }
        },
    },
}
</script>
